import type { AxiosResponse } from 'axios';
import type { AppUser, CurrentUserInfo, EmploymentStatus } from '@/shared/types/user';
import axiosClient from './axiosClient';
import type { ApiEnvelope } from './common';

export async function fetchMyInfo(): Promise<CurrentUserInfo> {
  const res = await axiosClient.get<ApiEnvelope<CurrentUserInfo>>(`/api/users/me`);
  if (res.data.errorMessage) {
    throw new Error(res.data.errorMessage);
  }
  return res.data.data;
}

export async function fetchUsers(params?: {
  employmentStatus?: EmploymentStatus;
}): Promise<AppUser[]> {
  const res = await axiosClient.get<ApiEnvelope<AppUser[]>>(`/api/users`, {
    params: params?.employmentStatus ? { employmentStatus: params.employmentStatus } : undefined,
  });
  if (res.data.errorMessage) {
    throw new Error(res.data.errorMessage);
  }
  return res.data.data;
}

export async function fetchUserPhoto(
  userId: string,
  size: number,
  signal?: AbortSignal,
): Promise<AxiosResponse<Blob>> {
  const res = await axiosClient.get(`/api/users/${userId}/photo?size=${size}`, {
    responseType: 'blob',
    withCredentials: true,
    ...(signal ? { signal } : {}),
    validateStatus: (s) => s < 500,
  });

  return res;
}

/**
 * 대체 결재자 지정
 * 본인 또는 HRup 조직관리 권한자만 호출할 수 있다. 대체자는 재직(ACTIVE) 상태여야 하며,
 * 휴직·퇴사자나 본인 지정은 400으로 거부된다.
 */
export async function setApprovalSubstitute(
  userId: string,
  substituteId: string,
): Promise<AppUser> {
  const res = await axiosClient.put<ApiEnvelope<AppUser>>(
    `/api/users/${userId}/approval-substitute`,
    { substituteId },
  );
  if (res.data.errorMessage) {
    throw new Error(res.data.errorMessage);
  }
  return res.data.data;
}

/**
 * 대체 결재자 해제
 * 본인 또는 HRup 조직관리 권한자만 호출할 수 있다. 성공 시 응답 본문 없음(204).
 */
export async function removeApprovalSubstitute(userId: string): Promise<void> {
  await axiosClient.delete(`/api/users/${userId}/approval-substitute`);
}
